import { cn } from "@/lib/utils";

type Props = {
  /** Variant : `mark` only renders the square mark, `wordmark` adds the text. */
  variant?: "mark" | "wordmark";
  /** Tailwind size class applied to the square mark. Defaults to size-7. */
  className?: string;
};

/**
 * Boursicote logo — square primary tile with a bold white "B" and a green
 * upward chevron in the top-right corner. Mirrors public/favicon.svg so the
 * sidebar and the browser tab stay visually consistent.
 *
 * Colours come from the theme palette (`bg-primary` and a hard-coded green
 * for the gain marker that stays readable in both light and dark themes).
 */
export function BoursicoteLogo({ variant = "mark", className }: Props) {
  const mark = (
    <span
      className={cn(
        "relative inline-flex items-center justify-center rounded-md bg-primary text-primary-foreground font-extrabold",
        "size-7 text-base",
        className
      )}
      aria-hidden="true"
    >
      B
      {/* Gain chevron, absolutely positioned to mimic the favicon corner. */}
      <svg
        viewBox="0 0 16 16"
        className="absolute -top-1 -right-1 size-3"
        fill="none"
        stroke="hsl(150 65% 55%)"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polyline points="2,10 8,4 14,10" />
      </svg>
    </span>
  );

  if (variant === "mark") return mark;

  return (
    <span className="inline-flex items-baseline gap-2">
      {mark}
      <span className="font-semibold tracking-tight text-foreground">
        Boursicote<span className="text-primary">.fr</span>
      </span>
    </span>
  );
}
